/**
 * IndustryRevenueComparisonChart
 *
 * Clustered bar chart: one bar per company per year, plus industry median
 * as a bold dashed line overlay. Matches Financial Insight Dashboard style.
 */
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { formatFiscalYear, formatCurrencyValue } from '../../financial/utils/formatters';
import type { RegionConfig } from '../../../types/financial';
import type { CompanyTimeSeries, IndustryBenchmarkRecord } from '../../../types/industryResearch';

const COMPANY_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#06b6d4', '#f97316', '#84cc16',
  '#ec4899', '#14b8a6',
];

interface Props {
  companies: CompanyTimeSeries[];
  medianData: IndustryBenchmarkRecord[];
  region?: RegionConfig;
}

export default function IndustryRevenueComparisonChart({ companies, medianData, region }: Props) {
  const years = medianData.map((d) => d.year);

  const chartData = years.map((yr) => {
    const medianRec = medianData.find((d) => d.year === yr);
    const entry: Record<string, number | string | null> = {
      year: formatFiscalYear(yr, region),
      _median: medianRec?.total_revenue ?? null,
    };
    companies.forEach((co) => {
      const rec = co.data.find((d) => d.year === yr);
      entry[co.company_name] = rec?.total_revenue ?? rec?.revenue_from_operations ?? null;
    });
    return entry;
  });

  const unit = region?.currency?.unit_abbr ?? '';

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold text-neutral-800">Revenue Comparison ({unit})</h3>
        <p className="text-xs text-neutral-500 mt-0.5">Revenue per company vs industry median</p>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 0 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis
              width={64}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatCurrencyValue(v, region, true)}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatCurrencyValue(value, region),
                name === '_median' ? 'Industry Median' : name,
              ]}
            />
            <Legend
              formatter={(v) => (v === '_median' ? 'Industry Median' : v)}
              wrapperStyle={{ fontSize: 11 }}
            />
            {companies.map((co, i) => (
              <Bar
                key={co.company_name}
                dataKey={co.company_name}
                fill={COMPANY_COLORS[i % COMPANY_COLORS.length]}
                radius={[3, 3, 0, 0]}
                maxBarSize={40}
              />
            ))}
            <Line
              dataKey="_median"
              name="_median"
              stroke="#1f2937"
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={false}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
}
